import React, { useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCards } from "swiper/modules";

import logo from "../../../assets/images/theme/logo/icon.png";

import "swiper/css";
import "swiper/css/effect-cards";
import "./MarketplaceFlowSlider.css";

const marketplaceFlows = [
  {
    id: "cdl",
    title: "Regional CDL Driver",
    company: "ACME Logistics",
    badge: "Now Hiring",
    category: "Trucking / CDL",
    accent: "purple",
    thumbnailUrl: "https://uploads.jobpeeks.com/home/cdl.jpg",
    audience: "Driver audience",
    slots: ["Top slot", "30 days", "Live price"],
    feeds: [
      {
        name: "Trucking Jobs Feed",
        note: "Top sponsored slot",
        reach: "24.5K",
      },
      { name: "CDL Driver Feed", note: "Driver audience", reach: "18.7K" },
      { name: "Logistics Careers", note: "Sponsored rotation", reach: "15.2K" },
      {
        name: "Regional Driver Feed",
        note: "Selected publisher",
        reach: "12.3K",
      },
    ],
  },
  {
    id: "healthcare",
    title: "Emergency Room Nurse",
    company: "Northview Health",
    badge: "Urgent Role",
    category: "Healthcare",
    accent: "blue",
    thumbnailUrl: "https://uploads.jobpeeks.com/home/healthcare.jpg",
    audience: "Clinical audience",
    slots: ["Top slot", "60 days", "Live price"],
    feeds: [
      { name: "Nursing Jobs Feed", note: "Top sponsored slot", reach: "31.4K" },
      { name: "Healthcare Careers", note: "Clinical talent", reach: "26.8K" },
      {
        name: "Hospital Hiring Feed",
        note: "Sponsored rotation",
        reach: "19.6K",
      },
      { name: "Care Team Jobs", note: "Selected publisher", reach: "14.2K" },
    ],
  },
  {
    id: "retail",
    title: "Store Assistant Manager",
    company: "BrightMart",
    badge: "Hiring Today",
    category: "Retail",
    accent: "pink",
    thumbnailUrl: "https://uploads.jobpeeks.com/home/retail.jpg",
    audience: "Retail audience",
    slots: ["Rotation", "30 days", "Live price"],
    feeds: [
      { name: "Retail Jobs Feed", note: "Sponsored rotation", reach: "22.1K" },
      { name: "Store Careers", note: "Retail workers", reach: "17.9K" },
      { name: "Hourly Jobs Feed", note: "Selected publisher", reach: "15.6K" },
      { name: "Local Retail Hiring", note: "Store audience", reach: "10.8K" },
    ],
  },
  {
    id: "warehouse",
    title: "Warehouse Associate",
    company: "Metro Fulfillment",
    badge: "Multiple Openings",
    category: "Warehouse",
    accent: "cyan",
    thumbnailUrl: "https://uploads.jobpeeks.com/home/warehouse.jpg",
    audience: "Warehouse audience",
    slots: ["Top slot", "30 days", "Live price"],
    feeds: [
      {
        name: "Warehouse Jobs Feed",
        note: "Top sponsored slot",
        reach: "28.9K",
      },
      { name: "Logistics Hiring", note: "Operations talent", reach: "21.3K" },
      {
        name: "Fulfillment Careers",
        note: "Sponsored rotation",
        reach: "16.7K",
      },
      { name: "Shift Work Feed", note: "Selected publisher", reach: "13.4K" },
    ],
  },
  {
    id: "construction",
    title: "Construction Supervisor",
    company: "Summit Builders",
    badge: "Crew Lead",
    category: "Construction",
    accent: "orange",
    thumbnailUrl: "https://uploads.jobpeeks.com/home/construction.jpg",
    audience: "Trade audience",
    slots: ["Top slot", "60 days", "Live price"],
    feeds: [
      {
        name: "Construction Jobs Feed",
        note: "Top sponsored slot",
        reach: "20.8K",
      },
      { name: "Skilled Trades Feed", note: "Trade audience", reach: "18.1K" },
      { name: "Builder Careers", note: "Sponsored rotation", reach: "13.9K" },
      { name: "Local Crew Hiring", note: "Selected publisher", reach: "11.4K" },
    ],
  },
];

const MarketplaceFlowSlider = ({ flows = marketplaceFlows }) => {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const safeFlows = flows?.length ? flows : marketplaceFlows;
  const activeFlow = safeFlows[activeIndex] || safeFlows[0];

  const feedChips = useMemo(
    () =>
      activeFlow.feeds
        .slice(0, 3)
        .map((feed) => feed.name.replace(" Feed", "")),
    [activeFlow],
  );

  const goToSlide = (index) => {
    swiperRef.current?.slideTo(index);
    setActiveIndex(index);
  };

  const feedTilesFor = (feedIndex) => {
    const sponsoredIndex = feedIndex % 4;

    return Array.from({ length: 4 }, (_, tileIndex) => ({
      id: `${feedIndex}-${tileIndex}`,
      sponsored: tileIndex === sponsoredIndex,
    }));
  };

  return (
    <div
      className="marketplace-flow-slider"
      aria-label="Job video marketplace distribution flow"
    >
      <div className="marketplace-flow-slider__video">
        <div className="marketplace-flow-slider__label">
          Scrollable Job Video
        </div>
        <Swiper
          effect="cards"
          grabCursor
          modules={[EffectCards]}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          className="marketplace-video-swiper"
        >
          {safeFlows.map((flow) => (
            <SwiperSlide key={flow.id}>
              <div
                className={`marketplace-video-card marketplace-video-card--${flow.accent}`}
              >
                <img
                  src={flow.thumbnailUrl}
                  alt={`${flow.title} job video thumbnail`}
                  className="marketplace-video-card__thumbnail"
                  loading="lazy"
                />
                <div className="marketplace-video-card__brand">
                  <img src={logo} alt="" />
                  <span>Job Peeks</span>
                </div>
                <div
                  className="marketplace-video-card__actions"
                  aria-hidden="true"
                >
                  <span>
                    <i className="feather-heart" />
                    128
                  </span>
                  <span>
                    <i className="feather-message-circle" />
                    23
                  </span>
                  <span>
                    <i className="feather-send" />
                    16
                  </span>
                </div>
                <div className="marketplace-video-card__body">
                  <span className="marketplace-video-card__badge">
                    {flow.badge}
                  </span>
                  <h3>{flow.title}</h3>
                  <p>{flow.company}</p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div
          className="marketplace-flow-slider__dots"
          aria-label="Select sample job video"
        >
          {safeFlows.map((flow, index) => (
            <button
              key={flow.id}
              type="button"
              className={index === activeIndex ? "active" : ""}
              aria-label={`Show ${flow.category} marketplace flow`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>

      <div className="marketplace-flow-slider__connector" aria-hidden="true" />

      <div className="marketplace-flow-slider__marketplace">
        <div className="marketplace-flow-slider__panel">
          <div className="marketplace-flow-slider__panel-header">
            <div className="marketplace-flow-slider__panel-logo">
              <img src={logo} alt="" />
              <span>Marketplace</span>
            </div>
            <span className="marketplace-flow-slider__panel-badge">
              {activeFlow.category}
            </span>
          </div>

          <div className="marketplace-flow-slider__step">
            <span className="marketplace-flow-slider__step-icon">
              <i className="feather-grid" />
            </span>
            <span className="marketplace-flow-slider__step-copy">
              <strong>Choose Publisher Feeds</strong>
              <span className="marketplace-flow-slider__chips">
                {feedChips.map((chip) => (
                  <span key={chip}>{chip}</span>
                ))}
              </span>
            </span>
            <span className="marketplace-flow-slider__status">
              <i className="feather-check" />
            </span>
          </div>

          <div className="marketplace-flow-slider__step">
            <span className="marketplace-flow-slider__step-icon">
              <i className="feather-dollar-sign" />
            </span>
            <span className="marketplace-flow-slider__step-copy">
              <strong>Review Sponsored Slots</strong>
              <span className="marketplace-flow-slider__chips">
                {activeFlow.slots.map((slot) => (
                  <span key={slot}>{slot}</span>
                ))}
              </span>
            </span>
            <span className="marketplace-flow-slider__status">
              <i className="feather-check" />
            </span>
          </div>

          <div className="marketplace-flow-slider__step">
            <span className="marketplace-flow-slider__step-icon">
              <i className="feather-shopping-cart" />
            </span>
            <span className="marketplace-flow-slider__step-copy">
              <strong>Checkout and Go Live</strong>
              <span className="marketplace-flow-slider__chips">
                <span>Selected feeds</span>
                <span>Placement analytics</span>
              </span>
            </span>
            <span className="marketplace-flow-slider__status">
              <i className="feather-lock" />
            </span>
          </div>
        </div>
      </div>

      <div
        className="marketplace-flow-slider__connector marketplace-flow-slider__connector--right"
        aria-hidden="true"
      />

      <div className="marketplace-flow-slider__feeds">
        <div className="marketplace-flow-slider__label">
          Scrollable Publisher Feed
        </div>
        <div className="marketplace-feed-stack">
          {activeFlow.feeds.map((feed, index) => (
            <div
              key={feed.name}
              className={`marketplace-feed-card marketplace-feed-card--${
                index + 1
              }`}
            >
              <div className="marketplace-feed-card__header">
                <h3>{feed.name}</h3>
                <span>
                  <i className="feather-users" />
                  {feed.reach}
                </span>
              </div>
              <div className="marketplace-feed-card__grid">
                {feedTilesFor(index).map((tile, tileIndex) => (
                  <div
                    key={tile.id}
                    className={`marketplace-feed-card__tile ${
                      tile.sponsored
                        ? "marketplace-feed-card__tile--sponsored"
                        : `marketplace-feed-card__tile--organic marketplace-feed-card__tile--organic-${
                            tileIndex + 1
                          }`
                    }`}
                  >
                    {tile.sponsored ? (
                      <>
                        <img
                          src={activeFlow.thumbnailUrl}
                          alt={`${activeFlow.title} sponsored video`}
                          loading="lazy"
                        />
                        <span>Ad</span>
                      </>
                    ) : (
                      <i className="feather-play" />
                    )}
                  </div>
                ))}
              </div>
              <div className="marketplace-feed-card__meta">
                <span>{feed.note}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketplaceFlowSlider;
